import React, { useState, useEffect } from "react";
import { isEmail } from "../../utils";
import { toast } from "react-toastify";
import "../../style/venders.css";

const VendorForm = ({ vendor, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!vendor;

  useEffect(() => {
    if (vendor) {
      setFormData({
        name: vendor.name || "",
        email: vendor.contact_email || vendor.email || "",
        address: vendor.address || "",
        phone: vendor.phone_number || vendor.phone || "",
      });
    }
  }, [vendor]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (formData.email.trim() && !isEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.phone.trim()) {
      const phoneRegex = /^\+?[1-9]\d{0,15}$/;
      if (!phoneRegex.test(formData.phone.replace(/\s/g, ""))) {
        newErrors.phone = "Please enter a valid phone number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const cleanedData = {
        name: formData.name.trim(),
        contact_email: formData.email.trim(),
        address: formData.address.trim(),
        phone_number: formData.phone.trim(),
      };

      await onSave(cleanedData);
    } catch (error) {
      console.error("Error saving Supplier:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "An error occurred while saving the Supplier. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="vendor-form-modal">
        <div className="vendor-form-modal-content no-scrollbar">
          {/* Supplier Form Header */}
          <div className="vendor-form-modal-header">
            <h2 className="vendor-modal-title">
              {isEditing ? "Edit Supplier" : "Add New Supplier"}
            </h2>
            <p className="vendor-modal-subtitle">
              {isEditing
                ? "Update Supplier information and contact details."
                : "Add a new Supplier to your supplier network"}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="vendor-form">
            {/* Supplier Form Body */}
            <div className="vendor-modal-body">
              <div className="vendor-form-group">
                <label htmlFor="name" className="vendor-form-label">
                  Supplier Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`vendor-form-input ${
                    errors.name ? "vendor-form-input-error" : ""
                  }`}
                  placeholder="Enter Supplier name"
                  required
                />
                {errors.name && (
                  <div className="vendor-form-error">{errors.name}</div>
                )}
              </div>

              <div className="vendor-form-group">
                <label htmlFor="email" className="vendor-form-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`vendor-form-input ${
                    errors.email ? "vendor-form-input-error" : ""
                  }`}
                  placeholder="Enter Supplier email address"
                />
                {errors.email && (
                  <div className="vendor-form-error">{errors.email}</div>
                )}
              </div>

              <div className="vendor-form-group">
                <label htmlFor="phone" className="vendor-form-label">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`vendor-form-input ${
                    errors.phone ? "vendor-form-input-error" : ""
                  }`}
                  placeholder="Enter Supplier phone number"
                />
                {errors.phone && (
                  <div className="vendor-form-error">{errors.phone}</div>
                )}
              </div>

              <div className="vendor-form-group">
                <label htmlFor="address" className="vendor-form-label">
                  Address
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="vendor-form-input vendor-form-textarea"
                  rows="3"
                  placeholder="Enter complete address"
                ></textarea>
              </div>
            </div>

            {/* Supplier Form Footer */}
            <div className="vendor-modal-footer">
              <button
                type="button"
                className="vendor-btn vendor-btn-cancel"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="vendor-btn vendor-btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="vendor-spinner"></span>
                    Saving...
                  </>
                ) : isEditing ? (
                  "Update Supplier"
                ) : (
                  "Add Supplier"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default VendorForm;
